import { NextPageContext } from 'next';
import { stringify } from 'querystring';
import { postFilterDescription } from 'lib/filters';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { Explore } from 'features/Explore';
import { isEmpty } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Post } from 'lib/types';
import { serialize, ucFirst } from 'lib/helpers';
import { queryToCriteria } from 'lib/utils/filterUtils';
import { PostSearchCriteria } from 'lib/types/filters';
import { PostServiceMeta } from 'server/api/types';
import { getLogger } from 'server/logger';
import { getCurrentUserFromContext } from 'server/api/services/auth';
import { webAssetUrl } from 'lib/utils/imageUtils';
import { withTimeout } from 'lib/utils/timeoutUtils';
import { PostSearch } from 'server/es/indexes/posts/search';
import { UserListsProvider } from 'lib/contexts/userListsContext';
import { TeamsProvider } from 'lib/contexts/teamsContext';
import { RolesProvider } from 'lib/contexts/rolesContext';

const logger = getLogger(module, 'pages/explore');

/** Give up on SSR'd posts after this. We'll get them on the client instead */
const TIMEOUT_MS = 350;

export const getServerSideProps = async (context: NextPageContext) => {
  const { query } = context;
  const baseUrl = 'https://gondola.cc/explore';
  const url = !isEmpty(query) ? `${baseUrl}?${stringify(query)}` : baseUrl;

  let posts: Post[] | null = null;
  let meta: PostServiceMeta | null = null;
  const criteria = queryToCriteria(query);
  const title = postFilterDescription(query, 'Posts');

  try {
    const currentUser = await getCurrentUserFromContext(context);

    if (currentUser?.id) {
      // If there is a current user, we'll fetch posts via the API after the page loads
      // This keeps the page load fast for logged-in users, but puts posts on the page
      // for anonymous users (including crawlers)
      return {
        props: {
          title,
          url,
          posts: null,
          criteria: serialize(criteria),
          meta: null,
        },
      };
    }

    const response = await withTimeout(PostSearch.exploreSearch({
      ...criteria,
    }, currentUser), TIMEOUT_MS) as any;

    posts = (response.data || []) as Post[];
    meta = response.meta as PostServiceMeta;
    logger.info(`Succesfully fetched ${posts?.length} posts server-side`);
  } catch (err) {
    logger.info(`Timed out (or errored) fetching posts server-side ${err}`);
    posts = null;
    meta = null;
  }

  return {
    props: {
      title,
      url,
      posts: serialize(posts),
      criteria: serialize(criteria),
      meta: serialize(meta),
    },
  };
};

interface Props {
  title: string;
  url: string;
  posts: Post[] | null;
  criteria: PostSearchCriteria;
  meta: PostServiceMeta;
}

const ExplorePage = ({
  title, url, posts, criteria, meta,
}: Props) => {
  const router = useRouter();
  const [richTitle, setRichTitle] = useState<string | ReactElement>(title || '');
  const [_title, setTitle] = useState<string>(title || '');

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    setTitle(postFilterDescription(router.query, 'posts') as string);
    if (isEmpty(router.query)) {
      setRichTitle('');
    } else {
      setRichTitle(postFilterDescription(router.query, 'Posts', true));
    }
  }, [router.query, router.isReady]);

  return (
    <>
      <GondolaHead
        title={ucFirst(title) || 'Find social media posts, credits, and analytics on Gondola'}
        description={
          `Search for social media posts by creator, account, platform, type, tag, event, keyword, and more. ${ucFirst(title)}`
        }
        url={url}
        cardUrl={webAssetUrl('meta-data-cards/explore.png')}
      />
      <RolesProvider>
        <TeamsProvider>
          <UserListsProvider>
            <Explore
              title={_title}
              richTitle={richTitle}
              serverPosts={posts}
              serverCriteria={criteria}
              serverMeta={meta}
            />
          </UserListsProvider>
        </TeamsProvider>
      </RolesProvider>
    </>
  );
};

export default ExplorePage;
